import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { canSearchPatientUhid } from '../../lib/patient-uhid-search';
import { createOtCase, fetchOtCase, fetchOtCases, fetchOtLookups, scheduleOtCase, updateOtCaseStatus } from './ot.api';
import type { OtCaseStatus, OtListFilters, OtLookupExtra, OtSchedulePayload } from './types';

export const useOtCases = (filters: OtListFilters) => useQuery({ queryKey: ['ot', 'cases', filters], queryFn: () => fetchOtCases(filters) });
export const useOtCase = (id?: string) => useQuery({ queryKey: ['ot', 'case', id], queryFn: () => fetchOtCase(id!), enabled: Boolean(id) });
export const useOtLookup = (type: string, search = '', extra: OtLookupExtra = {}) => useQuery({
  queryKey: ['ot', 'lookup', type, search, extra],
  queryFn: () => fetchOtLookups(type, search, extra),
  enabled: type !== 'patients' || canSearchPatientUhid(search),
  ...(type === 'patients' ? { staleTime: 0, refetchOnMount: 'always' as const } : { staleTime: 60_000 }),
});

export const useCreateOtCase = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: createOtCase, onSuccess: () => void client.invalidateQueries({ queryKey: ['ot'] }) });
};

export const useScheduleOtCase = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: OtSchedulePayload }) => scheduleOtCase(id, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ot'] }),
  });
};

export const useUpdateOtCaseStatus = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: OtCaseStatus }) => updateOtCaseStatus(id, status),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ot'] }),
  });
};
